import useChargingService from '../../../hooks/charging-services/useChargingService.ts';
import useUpdateChargingServiceMetadata from '../../../hooks/charging-services/useUpdateChargingServiceMetadata.ts';
import useUpdateChargingServiceTags from '../../../hooks/charging-services/useUpdateChargingServiceTags.ts';
import useEnableChargingService from '../../../hooks/charging-services/useEnableChargingService.ts';
import usePauseChargingService from '../../../hooks/charging-services/usePauseChargingService.ts';
import useDisableChargingService from '../../../hooks/charging-services/useDisableChargingService.ts';
import {
  UpdateChargingServiceMetadataRequest,
  UpdateChargingServiceTagsRequest,
} from '../../../api/requests/charging-service-request.ts';
import { notifyError } from '../../../utils/NotifyError.tsx';
import { LoadingOverlay, Modal } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import type { ErrorResponse } from '../../../api/responses/error-response.ts';
import MutateChargingServiceHeaderInfoForm, {
  MutateChargingServiceHeaderInfoFormValues,
} from '../../ui/places/MutateChargingServiceHeaderInfoForm.tsx';

interface UpdateChargingServiceHeaderInfoModalProps {
  opened: boolean;
  onClose: () => void;
  serviceId: string;
  onSuccess?: () => void;
}

const UpdateChargingServiceHeaderInfoModal = ({
  opened,
  onClose,
  serviceId,
  onSuccess,
}: UpdateChargingServiceHeaderInfoModalProps) => {
  const { data: chargingService, isLoading: isChargingServiceLoading } =
    useChargingService(serviceId);

  const { mutateAsync: updateChargingServiceMetadata } =
    useUpdateChargingServiceMetadata();
  const { mutateAsync: updateChargingServiceTags } =
    useUpdateChargingServiceTags();
  const { mutateAsync: enableChargingService } = useEnableChargingService();
  const { mutateAsync: pauseChargingService } = usePauseChargingService();
  const { mutateAsync: disableChargingService } = useDisableChargingService();

  const {
    mutate: handleSubmit,
    isPending: isUpdateTagsMetadataAndStatusPending,
  } = useMutation<
    void,
    ErrorResponse,
    MutateChargingServiceHeaderInfoFormValues
  >({
    mutationFn: async (formValues) => {
      const updateChargingServiceMetadataRequest: UpdateChargingServiceMetadataRequest =
        {
          brandId: chargingService.brandId,
          name: formValues.name,
          notes: chargingService.notes,
          phoneNumber: chargingService.phoneNumber,
          pricingSource: chargingService.pricingSource,
          serviceId: serviceId,
          website: chargingService.website,
        };

      await updateChargingServiceMetadata(updateChargingServiceMetadataRequest);

      const updateChargingServiceTagsRequest: UpdateChargingServiceTagsRequest =
        {
          serviceId: serviceId,
          tags: formValues.tags,
        };

      await updateChargingServiceTags(updateChargingServiceTagsRequest);

      if (formValues.serviceStatus === 'ONLINE') {
        await enableChargingService(serviceId);
      } else if (formValues.serviceStatus === 'TEMPORARY_OFFLINE') {
        await pauseChargingService(serviceId);
      } else {
        await disableChargingService(serviceId);
      }
    },
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      notifyError('更新服務資訊失敗', error.message);
    },
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="編輯充電資訊"
      centered
      closeOnClickOutside={false}
      size={440}
      styles={{
        title: { fontWeight: 700, fontSize: 16 },
        header: { paddingBottom: 24 },
      }}
    >
      {isChargingServiceLoading ? (
        <LoadingOverlay visible />
      ) : (
        <MutateChargingServiceHeaderInfoForm
          initialValues={{
            name: chargingService.name,
            serviceStatus: chargingService.serviceStatus,
            tags: chargingService.tags,
          }}
          onSubmit={handleSubmit}
          onCancel={onClose}
          isSubmitting={isUpdateTagsMetadataAndStatusPending}
        />
      )}
    </Modal>
  );
};

export { UpdateChargingServiceHeaderInfoModal };
